/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * FieldPay API
 * FieldPay API for managing organizations, opportunities, payouts and talent management
 * OpenAPI spec version: v1
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import * as axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type { CreateTalentTagDto } from "../types/createTalentTagDto";

import type { ListTagsParams } from "../types/listTagsParams";

import type { ProblemDetails } from "../types/problemDetails";

import type { TalentMemberSummaryDto } from "../types/talentMemberSummaryDto";

import type { TalentTagListItemDto } from "../types/talentTagListItemDto";

import type { TalentTagListItemDtoPaginatedResult } from "../types/talentTagListItemDtoPaginatedResult";

import type { UpdateTalentTagDto } from "../types/updateTalentTagDto";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * **Required Permission(s):** ORG_TALENT_TAG_LIST
 */
export const listTags = (
  params?: ListTagsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentTagListItemDtoPaginatedResult>> => {
  return axios.default.get(`/api/tags`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getListTagsQueryKey = (params?: ListTagsParams) => {
  return [`/api/tags`, ...(params ? [params] : [])] as const;
};

export const getListTagsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTags>>,
  TError = AxiosError<void>,
>(
  params?: ListTagsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTags>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTagsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTags>>> = ({
    signal,
  }) => listTags(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTags>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListTagsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTags>>
>;
export type ListTagsQueryError = AxiosError<void>;

export function useListTags<
  TData = Awaited<ReturnType<typeof listTags>>,
  TError = AxiosError<void>,
>(
  params?: ListTagsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTags>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTagsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_TALENT_TAG_CREATE
 */
export const createTalentTag = (
  createTalentTagDto: CreateTalentTagDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentTagListItemDto>> => {
  return axios.default.post(`/api/tags`, createTalentTagDto, options);
};

export const getCreateTalentTagMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTalentTag>>,
    TError,
    { data: CreateTalentTagDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTalentTag>>,
  TError,
  { data: CreateTalentTagDto },
  TContext
> => {
  const mutationKey = ["createTalentTag"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTalentTag>>,
    { data: CreateTalentTagDto }
  > = (props) => {
    const { data } = props ?? {};

    return createTalentTag(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTalentTagMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTalentTag>>
>;
export type CreateTalentTagMutationBody = CreateTalentTagDto;
export type CreateTalentTagMutationError = AxiosError<ProblemDetails | void>;

export const useCreateTalentTag = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTalentTag>>,
    TError,
    { data: CreateTalentTagDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTalentTag>>,
  TError,
  { data: CreateTalentTagDto },
  TContext
> => {
  const mutationOptions = getCreateTalentTagMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_TALENT_TAG_GET
 */
export const getTalentTagById = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentTagListItemDto>> => {
  return axios.default.get(`/api/tags/${id}`, options);
};

export const getGetTalentTagByIdQueryKey = (id?: string) => {
  return [`/api/tags/${id}`] as const;
};

export const getGetTalentTagByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTalentTagById>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentTagById>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTalentTagByIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTalentTagById>>
  > = ({ signal }) => getTalentTagById(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTalentTagById>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetTalentTagByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTalentTagById>>
>;
export type GetTalentTagByIdQueryError = AxiosError<void | ProblemDetails>;

export function useGetTalentTagById<
  TData = Awaited<ReturnType<typeof getTalentTagById>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentTagById>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTalentTagByIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_TALENT_TAG_UPDATE
 */
export const updateTalentTag = (
  id: string,
  updateTalentTagDto: UpdateTalentTagDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentTagListItemDto>> => {
  return axios.default.put(`/api/tags/${id}`, updateTalentTagDto, options);
};

export const getUpdateTalentTagMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTalentTag>>,
    TError,
    { id: string; data: UpdateTalentTagDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTalentTag>>,
  TError,
  { id: string; data: UpdateTalentTagDto },
  TContext
> => {
  const mutationKey = ["updateTalentTag"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTalentTag>>,
    { id: string; data: UpdateTalentTagDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateTalentTag(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTalentTagMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTalentTag>>
>;
export type UpdateTalentTagMutationBody = UpdateTalentTagDto;
export type UpdateTalentTagMutationError = AxiosError<ProblemDetails | void>;

export const useUpdateTalentTag = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTalentTag>>,
    TError,
    { id: string; data: UpdateTalentTagDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateTalentTag>>,
  TError,
  { id: string; data: UpdateTalentTagDto },
  TContext
> => {
  const mutationOptions = getUpdateTalentTagMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_TALENT_TAG_DELETE
 */
export const deleteTalentTag = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.default.delete(`/api/tags/${id}`, options);
};

export const getDeleteTalentTagMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTalentTag>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTalentTag>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteTalentTag"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTalentTag>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteTalentTag(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTalentTagMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTalentTag>>
>;

export type DeleteTalentTagMutationError = AxiosError<void | ProblemDetails>;

export const useDeleteTalentTag = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTalentTag>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteTalentTag>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteTalentTagMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_TALENT_TAG_GET
 */
export const getTalentMembersWithTag = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentMemberSummaryDto[]>> => {
  return axios.default.get(`/api/tags/${id}/talent-members`, options);
};

export const getGetTalentMembersWithTagQueryKey = (id?: string) => {
  return [`/api/tags/${id}/talent-members`] as const;
};

export const getGetTalentMembersWithTagQueryOptions = <
  TData = Awaited<ReturnType<typeof getTalentMembersWithTag>>,
  TError = AxiosError<void>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentMembersWithTag>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTalentMembersWithTagQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTalentMembersWithTag>>
  > = ({ signal }) => getTalentMembersWithTag(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTalentMembersWithTag>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetTalentMembersWithTagQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTalentMembersWithTag>>
>;
export type GetTalentMembersWithTagQueryError = AxiosError<void>;

export function useGetTalentMembersWithTag<
  TData = Awaited<ReturnType<typeof getTalentMembersWithTag>>,
  TError = AxiosError<void>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentMembersWithTag>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTalentMembersWithTagQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_TALENT_MEMBER_ADD_TAG
 */
export const addTagToTalentMember = (
  id: string,
  talentMemberId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.default.post(
    `/api/tags/${id}/talent-members/${talentMemberId}`,
    undefined,
    options,
  );
};

export const getAddTagToTalentMemberMutationOptions = <
  TError = AxiosError<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addTagToTalentMember>>,
    TError,
    { id: string; talentMemberId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addTagToTalentMember>>,
  TError,
  { id: string; talentMemberId: string },
  TContext
> => {
  const mutationKey = ["addTagToTalentMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addTagToTalentMember>>,
    { id: string; talentMemberId: string }
  > = (props) => {
    const { id, talentMemberId } = props ?? {};

    return addTagToTalentMember(id, talentMemberId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddTagToTalentMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof addTagToTalentMember>>
>;

export type AddTagToTalentMemberMutationError = AxiosError<void>;

export const useAddTagToTalentMember = <
  TError = AxiosError<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addTagToTalentMember>>,
    TError,
    { id: string; talentMemberId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof addTagToTalentMember>>,
  TError,
  { id: string; talentMemberId: string },
  TContext
> => {
  const mutationOptions = getAddTagToTalentMemberMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_TALENT_MEMBER_REMOVE_TAG
 */
export const removeTagFromTalentMember = (
  id: string,
  talentMemberId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.default.delete(
    `/api/tags/${id}/talent-members/${talentMemberId}`,
    options,
  );
};

export const getRemoveTagFromTalentMemberMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeTagFromTalentMember>>,
    TError,
    { id: string; talentMemberId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeTagFromTalentMember>>,
  TError,
  { id: string; talentMemberId: string },
  TContext
> => {
  const mutationKey = ["removeTagFromTalentMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeTagFromTalentMember>>,
    { id: string; talentMemberId: string }
  > = (props) => {
    const { id, talentMemberId } = props ?? {};

    return removeTagFromTalentMember(id, talentMemberId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveTagFromTalentMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeTagFromTalentMember>>
>;

export type RemoveTagFromTalentMemberMutationError =
  AxiosError<void | ProblemDetails>;

export const useRemoveTagFromTalentMember = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeTagFromTalentMember>>,
    TError,
    { id: string; talentMemberId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof removeTagFromTalentMember>>,
  TError,
  { id: string; talentMemberId: string },
  TContext
> => {
  const mutationOptions = getRemoveTagFromTalentMemberMutationOptions(options);

  return useMutation(mutationOptions);
};
